/**
 * AdminHomePage — the admin landing dashboard, traction first.
 *
 * Ordered by what matters at this stage of the product:
 *   1. Growth       — new users / projects / verifications / bids in the
 *                     selected window, each against the preceding window
 *   2. Trend        — the same four metrics over time
 *   3. Registrations— who signed up, and what they signed up as
 *   4. Needs you now— the operational queues, demoted but not hidden
 *
 * Two requests: /admin/traction (window-dependent) and /admin/home-summary
 * (window-independent). Changing the range refetches only the former.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  ButtonGroup,
  Alert,
  Skeleton,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People,
  Business,
  VerifiedUser,
  Description,
  Gavel,
  Assignment,
  Payment,
  RateReview,
  Forum,
  Refresh,
  CheckCircle,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  ErrorOutline,
} from '@mui/icons-material';

import adminService from '../services/adminService';
import BarRow from '../components/admin/BarRow';
import TrendChart from '../components/admin/TrendChart';
import { panelSx, panelHeaderSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, border } from '../theme/colors';
import { formatCurrency } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';

const REFRESH_MS = 60000;

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '12 months' },
];

/**
 * The four traction metrics. Colours are the validated 4-hue categorical set
 * (see TrendChart) — the same hue identifies a metric in its tile and in the
 * chart, so the two read as one thing.
 */
const METRICS = [
  { key: 'users', label: 'New registrations', icon: <People />, color: '#0d9488' },
  { key: 'projects', label: 'Projects created', icon: <Business />, color: '#2563eb' },
  { key: 'verifications', label: 'Verification requests', icon: <VerifiedUser />, color: '#d97706' },
  // Internal key is bid-named (CLAUDE.md); only the label says Quotations.
  { key: 'bids', label: 'Quotations submitted', icon: <Description />, color: '#7c3aed' },
];

const QUEUES = [
  { key: 'disputes', label: 'Open disputes', icon: <Gavel />, tone: statusTokens.error, to: '/admin/dashboard' },
  { key: 'applications', label: 'Applications to review', icon: <Assignment />, tone: statusTokens.warning, to: '/admin/home/applications' },
  { key: 'unassignedVerifications', label: 'Verifications unassigned', icon: <VerifiedUser />, tone: statusTokens.warning, to: '/admin/home/verifications' },
  { key: 'withdrawals', label: 'Withdrawals to approve', icon: <Payment />, tone: statusTokens.warning, to: '/admin/dashboard' },
  { key: 'milestoneReviews', label: 'Milestones awaiting review', icon: <RateReview />, tone: statusTokens.warning, to: '/admin/home/projects' },
  // A contractor asked to visit or sent a quotation and the client has been
  // silent 48h+. A phone call from a person is the fix; the Engagement tab
  // lists who.
  { key: 'clientSilence', label: 'Clients not replying to contractors (48h+)', icon: <Forum />, tone: statusTokens.error, to: '/admin/home/engagement' },
];

const titleise = (value) => String(value || '').replace(/_/g, ' ');

/** A traction metric: the window's count, its lifetime total, and the delta. */
const MetricTile = ({ metric, value, total, delta, note }) => {
  // null delta means "up from nothing" — a percentage there would be noise.
  const isNew = delta === null;
  const up = typeof delta === 'number' && delta > 0;
  const down = typeof delta === 'number' && delta < 0;

  return (
    <Grid item xs={12} sm={6} lg={3}>
      <Paper elevation={0} sx={{ ...panelSx, p: 2.25, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: `${metric.color}14`,
              color: metric.color,
              '& svg': { fontSize: 18 },
            }}
          >
            {metric.icon}
          </Box>
          <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 500 }}>
            {metric.label}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: 30, fontWeight: 700, color: text.primary, lineHeight: 1.1 }}>
          {(value || 0).toLocaleString()}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, minHeight: 22, flexWrap: 'wrap' }}>
          {isNew ? (
            <Chip
              size="small"
              label="New"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                color: statusTokens.successDark,
                bgcolor: statusTokens.successBg,
              }}
            />
          ) : (up || down) ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                color: up ? statusTokens.successDark : statusTokens.errorDark,
              }}
            >
              {up ? <TrendingUp sx={{ fontSize: 15 }} /> : <TrendingDown sx={{ fontSize: 15 }} />}
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {Math.abs(delta)}%
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 600 }}>
              No change
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: text.tertiary }}>
            vs previous period · {(total || 0).toLocaleString()} all time
          </Typography>
        </Box>
        {note && (
          <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 0.25 }}>
            {note}
          </Typography>
        )}
      </Paper>
    </Grid>
  );
};

const Panel = ({ title, subtitle, action, children }) => (
  <Paper elevation={0} sx={{ ...panelSx, height: '100%', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: text.primary }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: text.tertiary }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 2.5, flex: 1 }}>{children}</Box>
  </Paper>
);

const AdminHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [days, setDays] = useState(30);
  const [traction, setTraction] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    try {
      // The summary doesn't depend on the window, but refetching both keeps
      // the whole page consistent at one moment in time.
      const [t, s] = await Promise.all([
        adminService.getTraction({ days }),
        adminService.getHomeSummary(),
      ]);
      setTraction(t);
      setSummary(s);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load the dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ quiet: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const queues = summary?.queues;
  const money = summary?.money;
  const activeQueues = QUEUES.filter((q) => (queues?.[q.key] || 0) > 0);
  const firstName = (user?.profile?.firstName || user?.firstName || '').trim();

  const rangeLabel = RANGES.find((r) => r.days === days)?.label || `${days} days`;
  const byRole = traction?.byRole || [];
  const newByRole = (traction?.newByRole || []).reduce((acc, r) => {
    acc[r.role] = r.count;
    return acc;
  }, {});
  const roleMax = Math.max(1, ...byRole.map((r) => r.count));
  const roleTotal = byRole.reduce((a, r) => a + r.count, 0);
  const newBySource = traction?.newBySource || [];
  const sourceMax = Math.max(1, ...newBySource.map((s) => s.count));
  const sourceTotal = newBySource.reduce((a, s) => a + s.count, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: text.primary }}>
            {firstName ? `Welcome back, ${firstName}` : 'Platform overview'}
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mt: 0.25 }}>
            Traction over the last {rangeLabel.toLowerCase()}.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Filters in one row above the charts. */}
          <ButtonGroup size="small" disableElevation>
            {RANGES.map((r) => (
              <Button
                key={r.days}
                onClick={() => setDays(r.days)}
                variant={days === r.days ? 'contained' : 'outlined'}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                {r.label}
              </Button>
            ))}
          </ButtonGroup>
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => load({ quiet: true })}
              disabled={refreshing}
              size="small"
              sx={{ border: `1px solid ${border.light}`, borderRadius: 1.5 }}
            >
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={<Button color="inherit" size="small" onClick={() => load()}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} lg={3} key={i}>
                <Skeleton variant="rounded" height={132} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rounded" height={340} sx={{ borderRadius: 2, mb: 3 }} />
          <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />
        </>
      ) : (
        <>
          {/* 1 — Growth */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {METRICS.map((m) => (
              <MetricTile
                key={m.key}
                metric={m}
                value={traction?.current?.[m.key]}
                total={traction?.totals?.[m.key]}
                delta={traction?.deltaPct?.[m.key]}
                note={
                  // "Projects created" counts client-posted listings only; the
                  // working projects born from accepted quotations are noted
                  // separately so one deal never reads as two projects.
                  m.key === 'projects' && traction?.projectsFromQuotes?.current > 0
                    ? `+${traction.projectsFromQuotes.current} working project${traction.projectsFromQuotes.current === 1 ? '' : 's'} from accepted quotations`
                    : null
                }
              />
            ))}
          </Grid>

          {/* 2 — Trend, paired with lifetime totals so the row is used */}
          <Grid container spacing={2.5} alignItems="stretch" sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Panel
                title="Activity over time"
                subtitle={
                  traction?.range
                    ? `${traction.series.length} ${traction.range.bucket}${
                        traction.series.length === 1 ? '' : 's'
                      } · last ${rangeLabel.toLowerCase()}`
                    : null
                }
              >
                <TrendChart
                  series={traction?.series || []}
                  seriesConfig={METRICS.map((m) => ({ key: m.key, label: m.label, color: m.color }))}
                  bucket={traction?.range?.bucket || 'day'}
                />
              </Panel>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Panel title="All time" subtitle="Lifetime totals across the platform">
                <Grid container spacing={2.5}>
                  {METRICS.map((m) => (
                    <Grid item xs={6} key={m.key}>
                      <Typography
                        variant="caption"
                        sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
                      >
                        {m.label}
                      </Typography>
                      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>
                        {(traction?.totals?.[m.key] || 0).toLocaleString()}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2.5}>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
                    >
                      Held in escrow
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: text.primary }}>
                      {formatCurrency(money?.held || 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: text.tertiary, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' }}
                    >
                      Platform revenue
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, color: text.primary }}>
                      {formatCurrency(money?.revenue || 0)}
                    </Typography>
                  </Grid>
                </Grid>
              </Panel>
            </Grid>
          </Grid>

          {/* 3 — Registrations by role, full width so both bar columns fill */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Panel
                title="Registrations by role"
                subtitle={`${roleTotal.toLocaleString()} accounts · new in the last ${rangeLabel.toLowerCase()} shown in brackets`}
                action={
                  <Button
                    size="small"
                    endIcon={<ChevronRight />}
                    onClick={() => navigate('/admin/home/users')}
                    sx={{ textTransform: 'none', color: brand.primary, fontWeight: 500 }}
                  >
                    All users
                  </Button>
                }
              >
                {byRole.length === 0 ? (
                  <Typography variant="body2" sx={{ color: text.tertiary }}>
                    No accounts yet.
                  </Typography>
                ) : (
                  byRole.map((r) => (
                    <BarRow
                      key={r.role}
                      label={titleise(r.role)}
                      value={r.count}
                      max={roleMax}
                      caption={newByRole[r.role] ? `+${newByRole[r.role]} new` : null}
                      tooltip={`${r.count} of ${roleTotal} accounts${
                        newByRole[r.role] ? ` · ${newByRole[r.role]} joined in this period` : ''
                      }`}
                      onClick={() => navigate(`/admin/home/users?role=${r.role}`)}
                    />
                  ))
                )}
              </Panel>
            </Grid>

            {/* Where sign-ups came from: set at registration from utm/ref
                tags, the referrer, or 'direct'. Tag every push with
                ?utm_source=… and this becomes the scoreboard. */}
            <Grid item xs={12}>
              <Panel
                title={`Where sign-ups came from (last ${rangeLabel.toLowerCase()})`}
                subtitle="Tag links with ?utm_source=whatsapp, ?utm_source=facebook, … to see each push here. 'unknown' = registered before tracking existed or via the apps."
              >
                {newBySource.length === 0 ? (
                  <Typography variant="body2" sx={{ color: text.tertiary }}>No sign-ups in this period.</Typography>
                ) : (
                  newBySource.map((s) => (
                    <BarRow
                      key={s.source}
                      label={titleise(s.source)}
                      value={s.count}
                      max={sourceMax}
                      tooltip={`${s.count} of ${sourceTotal} sign-ups in this period`}
                    />
                  ))
                )}
              </Panel>
            </Grid>

          </Grid>

          {/* 4 — Operational queues, demoted */}
          <Typography
            variant="caption"
            sx={{
              color: text.tertiary,
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              display: 'block',
              mb: 1.25,
            }}
          >
            Needs you now
          </Typography>

          {activeQueues.length === 0 ? (
            <Alert severity="success" icon={<CheckCircle sx={{ fontSize: 18 }} />} sx={{ borderRadius: 2 }}>
              Every queue is clear — nothing is waiting on an admin.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {activeQueues.map((item) => (
                <Grid item xs={12} sm={6} lg={3} key={item.key}>
                  <Paper
                    elevation={0}
                    component="button"
                    type="button"
                    onClick={() => navigate(item.to)}
                    sx={{
                      ...panelSx,
                      p: 1.75,
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderLeft: `3px solid ${item.tone}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      '&:hover .queue-arrow': { color: item.tone, transform: 'translateX(2px)' },
                    }}
                  >
                    <Box sx={{ color: item.tone, display: 'grid', placeItems: 'center', '& svg': { fontSize: 18 } }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: text.primary }}>
                      {queues[item.key]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: text.secondary, flex: 1, minWidth: 0 }}>
                      {item.label}
                    </Typography>
                    <ChevronRight className="queue-arrow" sx={{ color: text.tertiary, transition: 'all 160ms', fontSize: 18 }} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}

          {money?.disputedCount > 0 && (
            <Alert
              severity="warning"
              icon={<ErrorOutline sx={{ fontSize: 18 }} />}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              {formatCurrency(money.disputedAmount)} across {money.disputedCount} escrow accounts is
              in dispute.
            </Alert>
          )}

          {/* Unconditional route to the legacy console. The queue tiles also
              link there, but only when their count is non-zero — with every
              queue clear there was no way back at all. */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => navigate('/admin/dashboard')}
              sx={{ textTransform: 'none', color: text.tertiary }}
            >
              Open the full admin console
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminHomePage;
